import { useCallback, useEffect, useMemo, useState } from 'react'
import { FaFileAlt, FaLayerGroup, FaPaperPlane, FaSignature, FaTasks } from 'react-icons/fa'
import { pdfjs } from 'react-pdf'
import { useSearchParams } from 'react-router-dom'
import api from '../../../helpers/apiRequester'
import { BatchesTab } from './batchesTab'
import { BulkSendTab } from './bulkSendTab'
import { useBatchList } from './hooks/useBatchPolling'
import { SendTab } from './sendTab'
import { SigningTab } from './signingTab'
import styles from './styles.module.scss'
import { ContractRow, ContractsTab, TAB_LABELS, TemplateRow, isContractsTab, parseFields } from './types'
import { VaultTab } from './vaultTab'

// Served from public/ as a plain .js file (copied by scripts/copy-pdf-worker.js
// via prestart/prebuild). The webpack-emitted .mjs asset breaks on staging:
// the host serves it as text/html, which module workers reject.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`

const TAB_ICONS: Record<ContractsTab, JSX.Element> = {
    send: <FaPaperPlane />,
    bulk: <FaLayerGroup />,
    signing: <FaSignature />,
    batches: <FaTasks />,
    vault: <FaFileAlt />,
}

// The vault is reached from a button beside the tab strip rather than a tab —
// it is a management area, not a step in the day-to-day send/track flow.
const TAB_ORDER: ContractsTab[] = ['send', 'bulk', 'signing', 'batches']

const isAwaiting = (c: ContractRow) =>
    c.delivery_type !== 'attachment' && (c.status === 'Sent' || c.status === 'Viewed')
const isComplete = (c: ContractRow) => c.status === 'Signed' || c.status === 'Delivered'

export const ContractsView = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    const tabParam = searchParams.get('tab')
    const tab: ContractsTab = isContractsTab(tabParam) ? tabParam : 'send'
    const preselectedTemplateId = Number(searchParams.get('template')) || null
    const selectedBatchId = Number(searchParams.get('batch')) || null
    const editTemplateId = Number(searchParams.get('edit')) || null

    const [templates, setTemplates] = useState<TemplateRow[]>([])
    const [templatesLoaded, setTemplatesLoaded] = useState(false)
    const [contracts, setContracts] = useState<ContractRow[]>([])
    const [contractsLoaded, setContractsLoaded] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)

    const { batches, loaded: batchesLoaded, error: batchesError, refresh: refreshBatches } = useBatchList(true)

    const fetchTemplates = useCallback(async () => {
        try {
            const response = await api({ url: 'contract-sign/templates/list', data: {} })
            if (response.data?.success) {
                const rows = (response.data.templates || []).map((t: any) => ({ ...t, fields: parseFields(t.fields) }))
                setTemplates(rows)
            }
        } catch (error) {
            console.error('Unable to fetch templates:', error)
            setLoadError('Could not load the contract templates. Please refresh to try again.')
        } finally {
            setTemplatesLoaded(true)
        }
    }, [])

    const fetchContracts = useCallback(async () => {
        try {
            const response = await api({ url: 'contract-sign/list', data: {} })
            if (response.data?.success) setContracts(response.data.contracts || [])
        } catch (error) {
            console.error('Unable to fetch contracts:', error)
            setLoadError('Could not load sent contracts. Please refresh to try again.')
        } finally {
            setContractsLoaded(true)
        }
    }, [])

    useEffect(() => {
        fetchTemplates()
        fetchContracts()
    }, [fetchTemplates, fetchContracts])

    // Batches finishing means new contracts exist; keep the signing list fresh.
    const runningCount = batches.filter((b) => b.status === 'running').length
    useEffect(() => {
        if (runningCount === 0 && batchesLoaded) fetchContracts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runningCount])

    const updateParams = useCallback(
        (updates: Record<string, string | null>) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev)
                    Object.entries(updates).forEach(([key, value]) => {
                        if (value === null || value === '') next.delete(key)
                        else next.set(key, value)
                    })
                    return next
                },
                { replace: true },
            )
        },
        [setSearchParams],
    )

    const selectTab = (next: ContractsTab) =>
        updateParams({ tab: next, template: null, status: null, batch: null, edit: null })

    const openSendWithTemplate = (templateId: number) =>
        updateParams({ tab: 'send', template: String(templateId), status: null, batch: null, edit: null })

    const openSigningWithStatus = (status: 'awaiting' | 'complete' | null) =>
        updateParams({ tab: 'signing', template: null, status, batch: null, edit: null })

    const openBatch = (batchId: number | null) => {
        updateParams({
            tab: 'batches',
            template: null,
            status: null,
            batch: batchId ? String(batchId) : null,
            edit: null,
        })
        refreshBatches()
    }

    // Jump into the field editor for a template that isn't sendable yet.
    const openTemplateEditor = (templateId: number) =>
        updateParams({ tab: 'vault', template: null, status: null, batch: null, edit: String(templateId) })

    const stats = useMemo(
        () => ({
            templates: templates.length,
            sent: contracts.length,
            awaiting: contracts.filter(isAwaiting).length,
            complete: contracts.filter(isComplete).length,
            running: runningCount,
        }),
        [templates, contracts, runningCount],
    )

    const tabCounts: Record<ContractsTab, number | null> = {
        send: null,
        bulk: null,
        signing: contractsLoaded ? contracts.length : null,
        batches: batchesLoaded ? batches.length : null,
        vault: templatesLoaded ? templates.length : null,
    }

    return (
        <div className={styles.wrap}>
            <p className={styles.intro}>
                Store reusable contract templates, mark where each recipient&apos;s details and signature go, then send
                a populated copy to one person or to a whole population — from a spreadsheet, a client search, an
                introducer filter or a list of identifiers. Contracts with a signature box are delivered as a personal
                signing link and tracked per bulk send; the rest are emailed as a finished PDF.
            </p>

            {loadError && <div className={styles.errorBanner}>{loadError}</div>}

            <div className={styles.summaryRow}>
                <button
                    type="button"
                    className={`${styles.summaryCard} ${tab === 'vault' ? styles.active : ''}`}
                    onClick={() => selectTab('vault')}
                >
                    <span className={styles.summaryValue}>{templatesLoaded ? stats.templates : '—'}</span>
                    <span className={styles.summaryLabel}>Contract templates</span>
                </button>
                <button
                    type="button"
                    className={`${styles.summaryCard} ${
                        tab === 'signing' && !searchParams.get('status') ? styles.active : ''
                    }`}
                    onClick={() => openSigningWithStatus(null)}
                >
                    <span className={styles.summaryValue}>{contractsLoaded ? stats.sent : '—'}</span>
                    <span className={styles.summaryLabel}>Contracts sent</span>
                </button>
                <button
                    type="button"
                    className={`${styles.summaryCard} ${
                        tab === 'signing' && searchParams.get('status') === 'awaiting' ? styles.active : ''
                    }`}
                    onClick={() => openSigningWithStatus('awaiting')}
                >
                    <span className={`${styles.summaryValue} ${stats.awaiting > 0 ? styles.accent : ''}`}>
                        {contractsLoaded ? stats.awaiting : '—'}
                    </span>
                    <span className={styles.summaryLabel}>Awaiting signature</span>
                </button>
                <button
                    type="button"
                    className={`${styles.summaryCard} ${
                        tab === 'signing' && searchParams.get('status') === 'complete' ? styles.active : ''
                    }`}
                    onClick={() => openSigningWithStatus('complete')}
                >
                    <span className={styles.summaryValue}>{contractsLoaded ? stats.complete : '—'}</span>
                    <span className={styles.summaryLabel}>Signed or delivered</span>
                </button>
                <button
                    type="button"
                    className={`${styles.summaryCard} ${tab === 'batches' && !selectedBatchId ? styles.active : ''}`}
                    onClick={() => openBatch(null)}
                >
                    <span className={`${styles.summaryValue} ${stats.running > 0 ? styles.accent : ''}`}>
                        {batchesLoaded ? (stats.running > 0 ? `${stats.running} running` : batches.length) : '—'}
                    </span>
                    <span className={styles.summaryLabel}>Bulk sends</span>
                </button>
            </div>

            <div className={styles.tabs} role="tablist" aria-label="Contracts sections">
                {TAB_ORDER.map((key) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={tab === key}
                        className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
                        onClick={() => selectTab(key)}
                    >
                        {TAB_ICONS[key]}
                        {TAB_LABELS[key]}
                        {tabCounts[key] !== null && <span className={styles.tabCount}>{tabCounts[key]}</span>}
                    </button>
                ))}
                <button
                    type="button"
                    className={`${styles.vaultBtn} ${tab === 'vault' ? styles.active : ''}`}
                    aria-pressed={tab === 'vault'}
                    onClick={() => selectTab('vault')}
                >
                    {TAB_ICONS.vault}
                    {TAB_LABELS.vault}
                    {tabCounts.vault !== null && <span className={styles.tabCount}>{tabCounts.vault}</span>}
                </button>
            </div>

            {tab === 'send' && (
                <SendTab
                    templates={templates}
                    templatesLoaded={templatesLoaded}
                    preselectedTemplateId={preselectedTemplateId}
                    onSent={() => {
                        fetchContracts()
                        refreshBatches()
                    }}
                    onGoToVault={() => selectTab('vault')}
                    onEditTemplate={openTemplateEditor}
                    onViewSigning={() => openSigningWithStatus(null)}
                    onViewBatch={openBatch}
                />
            )}

            {tab === 'bulk' && (
                <BulkSendTab
                    templates={templates}
                    templatesLoaded={templatesLoaded}
                    onBatchCreated={openBatch}
                    onGoToVault={() => selectTab('vault')}
                    onEditTemplate={openTemplateEditor}
                />
            )}

            {tab === 'signing' && (
                <SigningTab
                    contracts={contracts}
                    loaded={contractsLoaded}
                    onRefresh={fetchContracts}
                    statusFilterParam={searchParams.get('status')}
                    onStatusFilterChange={(value) => updateParams({ status: value })}
                    onViewBatch={openBatch}
                />
            )}

            {tab === 'batches' && (
                <BatchesTab
                    batches={batches}
                    loaded={batchesLoaded}
                    error={batchesError}
                    onRefresh={refreshBatches}
                    selectedBatchId={selectedBatchId}
                    onSelectBatch={openBatch}
                    onNewBulkSend={() => selectTab('bulk')}
                />
            )}

            {tab === 'vault' && (
                <VaultTab
                    templates={templates}
                    loaded={templatesLoaded}
                    onRefresh={fetchTemplates}
                    onTemplatesChange={setTemplates}
                    onSendTemplate={openSendWithTemplate}
                    editTemplateId={editTemplateId}
                    onEditHandled={() => updateParams({ edit: null })}
                />
            )}
        </div>
    )
}
